/**
 * Navigation group information.
 */
export interface NavigationGroupInfo {
  /**
   * The group ID.
   */
  readonly id: NavigationGroupId;

  /**
   * The optional class name of the navigation group. If not specified the group ID is used instead.
   */
  readonly className?: string;

  /**
   * The group name.
   */
  readonly name: string;

  /**
   * The group CSS color.
   */
  readonly color: string;
}

/**
 * Navigation group IDs.
 */
export type NavigationGroupId =
  | "Unknown"
  | "Core"
  | "Operations"
  | "Financial"
  | "Clinical"
  | "DataAndReporting"
  | "Admin"
  | "TPA"
  | "Documentation"
  | "Pharmacy";

/**
 * The navigation group.
 */
export const NavigationGroup: Readonly<Record<NavigationGroupId, NavigationGroupInfo>> = {
  Unknown: { id: "Unknown", name: "Unknown", color: "#000000" },
  Core: { id: "Core", name: "Core Adjudication", color: "#0071B3" },
  Operations: { id: "Operations", name: "Operations", color: "#7157c7" },
  Financial: { id: "Financial", name: "Financial", color: "#03826a" },
  Clinical: { id: "Clinical", name: "Clinical", color: "#2e3f9b" },
  DataAndReporting: { id: "DataAndReporting", className: "data-reporting", name: "Data & Reporting", color: "#c61349" },
  Admin: { id: "Admin", name: "Admin", color: "#d98912" },
  TPA: { id: "TPA", name: "Benefits Administration", color: "#135e6c" },
  Documentation: { id: "Documentation", name: "Documentation", color: "#00629c" },
  Pharmacy: { id: "Pharmacy", name: "Pharmacy", color: "#E0007A" },
};

/** The order for navigation group items on the dashboard. */
export const NavigationGroupDashboardOrder: Readonly<Record<NavigationGroupId, number>> = {
  Unknown: 9999,
  Core: 0,
  Operations: 1,
  Financial: 2,
  Clinical: 3,
  DataAndReporting: 4,
  Admin: 5,
  TPA: 6,
  Pharmacy: 7,
  Documentation: 9998,
};

/** The order for navigation group items on the menu. */
export const NavigationGroupMenuOrder: Readonly<Record<NavigationGroupId, number>> = {
  Unknown: 9999,
  Core: 0,
  Operations: 1,
  Financial: 2,
  Clinical: 3,
  DataAndReporting: 4,
  Admin: 5,
  TPA: 6,
  Pharmacy: 7,
  Documentation: 9998,
};

/**
 * A navigation item.
 */
export interface NavigationItem {
  /**
   * The navigatiton item custom abbreviation. Only the first two characters
   * are used. If not specified an abbreviation is generated from the title.
   */
  readonly abbreviation?: string;

  /**
   * The navigation item title.
   */
  readonly title: string;

  /**
   * The navigation item description.
   */
  readonly description?: string;

  /**
   * The relative path to the root.
   */
  readonly path: string;

  /**
   * Indicates whether the specified path is absolute (used by legacy modules).
   */
  readonly absolutePath?: boolean;

  /**
   * Indicates whether the item is for Capital Rx internal users only(hide items from external parties).
   */
  readonly internalUseOnly?: boolean;

  /**
   * Indicates whether the module is in beta.
   */
  readonly beta?: boolean;

  /**
   * The item group.
   */
  readonly group: NavigationGroupInfo;

  /**
   * The permissions required to access the module.
   */
  readonly permissions?: ReadonlyArray<string>;

  /**
   * The test ID for the item.
   */
  readonly testId?: string;
}

/**
 * The favorites API.
 */
export interface FavoritesApi {
  /**
   * Gets the current set of favorites.
   * @param authToken The authentication token of the user to get favorites for.
   * @returns A promise that returns the favorites.
   */
  getFavorites(authToken: string): Promise<ReadonlyArray<string>>;

  /**
   * Sets the favorites.
   * @param authToken The authentication token of teh user to set the favorites for.
   * @param favorites The favorites to set.
   * @returns A promise that returns the updated set of favorites.
   */
  setFavorites(authToken: string, favorites: ReadonlyArray<string>): Promise<ReadonlyArray<string>>;
}
